import {useEffect} from 'react';
import {useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import {GROUP_BY, PROJECT_PORTFOLIO_REPORT_PAGES} from '../ProjectPortfolioReport';
import {exportToCsv} from '../ProjectPortfolioReportCsvDownload';

const ProjectPortfolioMonthByMonthConnectedProjectDownload = ({
	viewer,
	closeModal,
	startDate,
	endDate,
	boolEyeOptions,
	selectedSingleValue,
}) => {
	const intl = useIntl();
	const currency = viewer.company.currency;
	const connectedProjects = viewer.company.financialBasedConnectedProjects.edges.map(edge => edge.node);

	useEffect(() => {
		if (viewer) {
			exportToCsv({
				rows: connectedProjects,
				grouping: GROUP_BY.CONNECTED_PROJECT,
				page: PROJECT_PORTFOLIO_REPORT_PAGES.MONTH_BY_MONTH,
				currency,
				boolEyeOptions,
				startDate,
				endDate,
				intl,
				selectedSingleValue,
			});
			closeModal();
		}
	}, [viewer]);

	return null;
};

export const projectPortfolioMonthByMonthConnectedProjectDownloadQuery = graphql`
	query ProjectPortfolioMonthByMonthConnectedProjectDownload_Query(
		$startYear: Int!
		$startMonth: Int!
		$startDay: Int!
		$endYear: Int!
		$endMonth: Int!
		$endDay: Int!
		$searchQuery: TaskSearchQueryType!
		$timeSearchQuery: TaskSearchQueryType!
		$expenseSearchQuery: TaskSearchQueryType!
		$selectedSingleValue: FinancialAggregatedNumber!
		$ignoreBaselineWinChance: Boolean
		$excludeOpportunityActuals: Boolean
	) {
		viewer {
			actualPersonId
			component(name: "project_portfolio_month_by_month_connected_project_csv_loader")
			...ProjectPortfolioMonthByMonthConnectedProjectDownload_viewer
				@arguments(
					searchQuery: $searchQuery
					timeSearchQuery: $timeSearchQuery
					expenseSearchQuery: $expenseSearchQuery
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					selectedSingleValue: $selectedSingleValue
					ignoreBaselineWinChance: $ignoreBaselineWinChance
					excludeOpportunityActuals: $excludeOpportunityActuals
				)
		}
	}
`;

export default createFragmentContainer(ProjectPortfolioMonthByMonthConnectedProjectDownload, {
	viewer: graphql`
		fragment ProjectPortfolioMonthByMonthConnectedProjectDownload_viewer on Viewer
		@argumentDefinitions(
			searchQuery: {type: "TaskSearchQueryType!"}
			timeSearchQuery: {type: "TaskSearchQueryType!"}
			expenseSearchQuery: {type: "TaskSearchQueryType!"}
			startYear: {type: "Int!"}
			startMonth: {type: "Int!"}
			startDay: {type: "Int!"}
			endYear: {type: "Int!"}
			endMonth: {type: "Int!"}
			endDay: {type: "Int!"}
			selectedSingleValue: {type: "FinancialAggregatedNumber!"}
			ignoreBaselineWinChance: {type: "Boolean"}
			excludeOpportunityActuals: {type: "Boolean"}
		) {
			id
			company {
				currency
				financialBasedConnectedProjects(
					searchQuery: $searchQuery
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					aggregateLevel: MONTH
					aggregates: [$selectedSingleValue]
					ignoreBaselineWinChance: $ignoreBaselineWinChance
					excludeOpportunityActuals: $excludeOpportunityActuals
				) {
					edges {
						node {
							id
							companyProjectGroupId
							color
							aggregatedFinancialNumbers(
								startYear: $startYear
								startMonth: $startMonth
								startDay: $startDay
								endYear: $endYear
								endMonth: $endMonth
								endDay: $endDay
								aggregateLevel: MONTH
								aggregates: [$selectedSingleValue]
								ignoreBaselineWinChance: $ignoreBaselineWinChance
								excludeOpportunityActuals: $excludeOpportunityActuals
							)
							timeAggregatedFinancialNumbers: aggregatedFinancialNumbers(
								searchQuery: $timeSearchQuery
								startYear: $startYear
								startMonth: $startMonth
								startDay: $startDay
								endYear: $endYear
								endMonth: $endMonth
								endDay: $endDay
								aggregateLevel: MONTH
								aggregates: [$selectedSingleValue]
								fetchOwnData: true
								ignoreBaselineWinChance: $ignoreBaselineWinChance
								excludeOpportunityActuals: $excludeOpportunityActuals
							)
							expenseAggregatedFinancialNumbers: aggregatedFinancialNumbers(
								searchQuery: $expenseSearchQuery
								startYear: $startYear
								startMonth: $startMonth
								startDay: $startDay
								endYear: $endYear
								endMonth: $endMonth
								endDay: $endDay
								aggregateLevel: MONTH
								aggregates: [$selectedSingleValue]
								fetchOwnData: true
								ignoreBaselineWinChance: $ignoreBaselineWinChance
								excludeOpportunityActuals: $excludeOpportunityActuals
							)
						}
					}
				}
			}
		}
	`,
});
